<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import { useMutation } from '@vue/apollo-composable';
import { useNotificationStore } from '@/stores/notification';
import { graphql } from '@/gql';
import { watchEffect } from 'vue';

const props = defineProps<{ projectId: string; deliverableId: string }>();
const emit = defineEmits<{ success: []; loading: [value: boolean] }>();

const { t } = useI18n({
    messages: {
        nl: {
            delete: 'Ontkoppel',
            delete_title: '@:deliverable ontkoppelen',
            delete_confirmation:
                'Weet je zeker dat je deze @.lower:deliverable wilt ontkoppelen?',
            delete_success: '@:deliverable succesvol ontkoppeld',
        },
        en: {
            delete: 'Unlink',
            delete_title: 'Unlink @:deliverable',
            delete_confirmation:
                'Are you sure you want to unlink this @.lower:deliverable?',
            delete_success: '@:deliverable unlinked',
        },
    },
});

const notificationStore = useNotificationStore();

const { mutate: unlinkDeliverable, loading } = useMutation(
    graphql(`
        mutation UnlinkDeliverable($input: UnLinkDeliverableInput!) {
            unlinkDeliverable(input: $input) {
                ok
            }
        }
    `),
    {
        refetchQueries: ['ProjectPhases'],
        awaitRefetchQueries: true,
    }
);

watchEffect(() => {
    emit('loading', loading.value);
});

async function handleDelete() {
    try {
        await unlinkDeliverable({
            input: {
                deliverableId: props.deliverableId,
                projectId: props.projectId,
            },
        });

        emit('success');
        notificationStore.add({
            type: 'success',
            message: t('delete_success'),
        });
    } catch {
        notificationStore.add({ type: 'error', message: t('delete_error') });
    }
}
</script>

<template>
    <ConfirmationDialog
        :title="t('delete_title')"
        :confirm-button-text="t('delete')"
        :text="t('delete_confirmation')"
        persistent
        @confirm="handleDelete"
    >
        <template #activator="{ props: activatorProps }">
            <slot name="activator" :props="activatorProps" :loading />
        </template>
    </ConfirmationDialog>
</template>
